import { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useHistory, useRouteMatch } from 'react-router-dom';
import { useFlags } from 'launchdarkly-react-client-sdk';
import {
  selectCollaboratedProjects,
  selectParticipatedProjects,
  selectAssociatedTeamsCount,
  isLDTeamManager,
  selectUserRole,
  selectUserType,
  isUserFacilitator,
} from 'state/User/userSlice';
import { PATHS, USER_TYPES } from 'utils/constants';
import usePermissions from 'Hooks/usePermissions';
import { PERMISSIONS } from 'utils/permissions';

const usePathPermission = () => {
  const inDashboardsModule = !!useRouteMatch({
    path: [
      PATHS.DASHBOARDS_LIST_PAGE,
      PATHS.DASHBOARD_PAGE,
      PATHS.DASHBOARD_DRILLDOWN_PAGE,
    ],
  });
  const inInsightsModule = !!useRouteMatch({
    path: PATHS.INSIGHTS,
  });
  const inRequestModule = !!useRouteMatch({
    path: [
      `${PATHS.REQUEST_PAGE}/:requestId`,
      PATHS.REQUEST_PAGE,
      PATHS.REQUESTS_LIST_PAGE,
    ],
  });
  const inProjectsModule = !!useRouteMatch({
    path: [
      `${PATHS.NEW_PROJECT_PAGE}/:templateId`,
      `${PATHS.PROJECT_PAGE}/:projectId`,
      `${PATHS.PROJECT_PAGE}/:projectId/tasks/:taskId`,
      PATHS.PROJECTS_LIST_PAGE,
    ],
  });
  const inTeamsModule = !!useRouteMatch({
    path: PATHS.TEAMS,
  });
  const inSettingsModule = !!useRouteMatch({
    path: [
      PATHS.SETTINGS,
      `${PATHS.EDIT_TEAM}/:teamType/:teamId`,
      `${PATHS.USER_PAGE}/:userId/:userType?`,
      `${PATHS.SETTINGS}${PATHS.FORM_PAGE}`,
      `${PATHS.VENDOR_PAGE}/:vendorId`,
      `${PATHS.SETTINGS}${PATHS.FORM_PAGE}/:formId`,
    ],
  });
  const isLearningScheduleModule = !!useRouteMatch({
    path: [PATHS.LEARNING_SCHEDULE_PAGE],
  });
  const inEvaluationModule = !!useRouteMatch({ path: PATHS.EVALUATIONS });
  const isStrategyModule = !!useRouteMatch({ path: PATHS.STRATEGY_GOALS });
  const {
    tasksAssignmentsBasedResourcesCapacity = false,
    performativIntegration = false,
    flexibleDashboards = false,
    hideLegacyInsights = true,
    flexibleDashboardShare = false,
  } = useFlags();
  const history = useHistory();
  const userType = useSelector(selectUserType);
  const userRole = useSelector(selectUserRole);
  const hasUser: boolean = useMemo<boolean>(
    () => Boolean(userRole && userType),
    [userRole, userType]
  );

  const [
    canAccessSettingsModule,
    canAccessTeamsModule,
    canAccessDashboardsModule,
    canAccessInsightsModule,
    canAccessLearningScheduleModule,
  ] = usePermissions([
    PERMISSIONS.ACCESS_SETTINGS_MODULE,
    PERMISSIONS.ACCESS_TEAMS_MODULE,
    PERMISSIONS.ACCESS_DASHBOARDS_MODULE,
    PERMISSIONS.ACCESS_INSIGHTS_MODULE,
    PERMISSIONS.ACCESS_LEARNING_SCHEDULE_MODULE,
  ]);
  const isFacilitator = useSelector(isUserFacilitator);
  const collaboratedProjects = useSelector(selectCollaboratedProjects);
  const participatedProjects = useSelector(selectParticipatedProjects);
  const associatedTeamsCount = useSelector(selectAssociatedTeamsCount);
  const isTeamManager = useSelector(isLDTeamManager);
  const projectsCurrentUserIsTakingPartIn =
    tasksAssignmentsBasedResourcesCapacity
      ? participatedProjects
      : collaboratedProjects;

  useEffect(() => {
    const hasPermission: boolean =
      flexibleDashboards &&
      (canAccessDashboardsModule ||
        (userType !== USER_TYPES.EXTERNAL && flexibleDashboardShare));
    if (inDashboardsModule && hasUser && !hasPermission) {
      history.push(PATHS.ROOT);
    }
  }, [
    history,
    inDashboardsModule,
    hasUser,
    flexibleDashboards,
    flexibleDashboardShare,
    userType,
    canAccessDashboardsModule,
  ]);

  useEffect(() => {
    const hasPermission: boolean =
      canAccessInsightsModule && !hideLegacyInsights;
    if (inInsightsModule && hasUser && !hasPermission) {
      history.push(PATHS.ROOT);
    }
  }, [
    history,
    inInsightsModule,
    hasUser,
    canAccessInsightsModule,
    hideLegacyInsights,
  ]);

  useEffect(() => {
    if (
      inRequestModule &&
      userType &&
      !(userType === USER_TYPES.L_D || userType === USER_TYPES.BUSINESS)
    ) {
      history.push(PATHS.ROOT);
    }
  }, [history, inRequestModule, userType]);

  useEffect(() => {
    if (
      inProjectsModule &&
      userType &&
      projectsCurrentUserIsTakingPartIn &&
      !(
        userType === USER_TYPES.L_D ||
        isFacilitator ||
        projectsCurrentUserIsTakingPartIn.length > 0
      )
    ) {
      history.push(PATHS.ROOT);
    }
  }, [
    projectsCurrentUserIsTakingPartIn,
    history,
    inProjectsModule,
    isFacilitator,
    userType,
  ]);

  useEffect(() => {
    if (
      inTeamsModule &&
      userType &&
      associatedTeamsCount &&
      !(
        userType === USER_TYPES.L_D &&
        (canAccessTeamsModule || associatedTeamsCount > 0)
      )
    ) {
      history.push(PATHS.ROOT);
    }
  }, [
    history,
    inTeamsModule,
    associatedTeamsCount,
    userType,
    canAccessTeamsModule,
  ]);

  useEffect(() => {
    if (inSettingsModule && hasUser && !canAccessSettingsModule) {
      history.push(PATHS.ROOT);
    }
  }, [history, inSettingsModule, hasUser, canAccessSettingsModule]);

  useEffect(() => {
    if (
      isLearningScheduleModule &&
      userType &&
      !canAccessLearningScheduleModule &&
      !isFacilitator &&
      !isTeamManager
    ) {
      history.push(PATHS.ROOT);
    }
  }, [
    history,
    isLearningScheduleModule,
    canAccessLearningScheduleModule,
    isFacilitator,
    userType,
    isTeamManager,
  ]);

  useEffect(() => {
    if (inEvaluationModule && hasUser && !performativIntegration) {
      history.push(PATHS.ROOT);
    }
  }, [history, inEvaluationModule, hasUser, performativIntegration]);

  useEffect(() => {
    if (isStrategyModule && userType === USER_TYPES.EXTERNAL) {
      history.push(PATHS.ROOT);
    }
  }, [history, isStrategyModule, userType]);
};

export default usePathPermission;
